'use client';

import { useEffect } from 'react';
import {
  isWxJwtValid,
  storeWxJwtInLocalStorage,
} from '@/lib/utils/wx-jwt-storage';

/**
 * Fetches the raw WX JWT from the server's short-lived handoff store and
 * writes it into localStorage for the WX Copilot / Navbar widgets.
 *
 * The flow is: WX callback endpoints stash the JWT in Redis under an opaque
 * id (id in an httpOnly cookie); the browser redirects into the app; this
 * component mounts in the root layout and calls GET /api/v1/wx-jwt-handoff
 * which reads Redis and returns `{ jwt }`. If Redis is down during the
 * callback, the callback writes a raw-JWT cookie as a fallback and the
 * endpoint returns that instead.
 *
 * Skips the server call entirely when localStorage already holds a valid
 * JWT (the common case — the handoff only matters on the very first
 * request after a fresh sign-in).
 */

// Module-level guard deduplicates concurrent calls within a single tab —
// e.g. React strict mode double-invoking effects in development.
let inFlight: Promise<void> | null = null;

async function syncJwtFromServer(): Promise<void> {
  try {
    const res = await fetch('/api/v1/wx-jwt-handoff', {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      return;
    }
    const data = (await res.json()) as { jwt?: string | null };
    if (data?.jwt) {
      storeWxJwtInLocalStorage(data.jwt);
    }
  } catch (err) {
    console.warn('[WxJwtHandoff] handoff fetch failed:', err);
  }
}

export function WxJwtHandoff() {
  useEffect(() => {
    // localStorage already has a valid token — no handoff needed.
    if (isWxJwtValid()) {
      return;
    }
    if (inFlight) {
      return;
    }
    inFlight = syncJwtFromServer().finally(() => {
      inFlight = null;
    });
  }, []);

  return null;
}
